import React, { useCallback, useEffect, useState } from "react";
import { Plus, Warehouse, MapPin, Boxes, Package, ArrowLeft, Minus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import { formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";
const CAPS = ["Picking", "Repotting", "Plant QC", "QC", "Packing", "Dispatch"];
const EMPTY = { name: "", type: "Owned", city: "", state: "", pincode: "", contact_name: "", phone: "", email: "", capabilities: [], brands: [], sla_days: 3, capacity: 100 };

export default function Hubs() {
  const { brands, selectedBrand, refreshHubs } = useBrand();
  const [hubs, setHubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [active, setActive] = useState(null);          // hub being managed
  const [offerings, setOfferings] = useState([]);
  const [products, setProducts] = useState([]);
  const [offOpen, setOffOpen] = useState(false);
  const [offForm, setOffForm] = useState({ sku: "", product_id: "", product_name: "", transfer_price: "", on_hand: "" });

  const load = useCallback(() => {
    const q = selectedBrand && selectedBrand !== "All Brands" ? `?brand=${encodeURIComponent(selectedBrand)}` : "";
    api.get(`/hubs${q}`).then((r) => setHubs(r.data)).catch(() => {});
  }, [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const loadOfferings = (hub) => api.get(`/hubs/${hub.id}/offerings`).then((r) => setOfferings(r.data)).catch(() => {});
  const openHub = (hub) => {
    setActive(hub);
    loadOfferings(hub);
    api.get(`/products${hub.brands?.length ? `?brand=${encodeURIComponent(hub.brands[0])}` : ""}`).then((r) => setProducts(r.data)).catch(() => {});
  };

  const toggleCap = (c) => setForm((f) => ({ ...f, capabilities: f.capabilities.includes(c) ? f.capabilities.filter((x) => x !== c) : [...f.capabilities, c] }));
  const toggleBrand = (b) => setForm((f) => ({ ...f, brands: f.brands.includes(b) ? f.brands.filter((x) => x !== b) : [...f.brands, b] }));

  const submit = async () => {
    if (!form.name || !form.pincode) return toast.error("Name and pincode required");
    try {
      await api.post("/hubs", { ...form, sla_days: Number(form.sla_days), capacity: Number(form.capacity) });
      toast.success("Hub created"); setOpen(false); setForm(EMPTY); load(); refreshHubs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addOffering = async () => {
    if (!offForm.sku) return toast.error("SKU required");
    try {
      await api.post(`/hubs/${active.id}/offerings`, {
        sku: offForm.sku, product_id: offForm.product_id, product_name: offForm.product_name,
        brand: active.brands?.[0] || "", transfer_price: Number(offForm.transfer_price || 0), on_hand: Number(offForm.on_hand || 0),
      });
      toast.success("Product offering added"); setOffOpen(false);
      setOffForm({ sku: "", product_id: "", product_name: "", transfer_price: "", on_hand: "" });
      loadOfferings(active); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const adjust = async (off, delta) => {
    try { await api.post(`/offerings/${off.id}/adjust?qty=${delta}&note=Manual`); loadOfferings(active); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ----- flat SKU list for the add-offering picker (product + variant SKUs)
  const skuOptions = [];
  products.forEach((p) => {
    const vs = p.variants || [];
    if (vs.length) vs.forEach((v) => skuOptions.push({ sku: v.sku, product_id: p.product_id, name: `${p.name} · ${v.name}` }));
    else skuOptions.push({ sku: p.sku, product_id: p.product_id, name: p.name });
  });

  if (active) {
    return (
      <div>
        <PageHeader title={active.name} subtitle={`${active.type} · ${active.city} ${active.pincode} · SLA ${active.sla_days}d`}
          actions={<div className="flex items-center gap-3">
            <button data-testid="add-offering-button" onClick={() => setOffOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> Add Product</button>
            <button data-testid="back-to-hubs" onClick={() => { setActive(null); load(); }} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors"><ArrowLeft size={15} /> Hubs</button>
          </div>} />
        <div className="p-8">
          {offerings.length === 0 ? <EmptyState icon={Package} title="No product offerings" hint="Add the products this hub can fulfill." /> : (
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-white/10">
                  <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3 text-right">Transfer ₹</th>
                  <th className="px-4 py-3 text-right">On hand</th><th className="px-4 py-3 text-right">Reserved</th><th className="px-4 py-3 text-right">Available</th><th className="px-4 py-3 text-center">Adjust</th>
                </tr></thead>
                <tbody>
                  {offerings.map((o) => (
                    <tr key={o.id} data-testid={`offering-${o.sku}`} className="border-b border-white/5">
                      <td className="px-4 py-3 text-zinc-200">{o.product_name || "—"}</td>
                      <td className="px-4 py-3 font-mono text-zinc-500">{o.sku}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-300">{formatINR(o.transfer_price)}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-300">{o.on_hand}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#FFD600]">{o.reserved}</td>
                      <td className={`px-4 py-3 text-right font-mono ${o.available <= o.reorder_level ? "text-[#FF6259]" : "text-[#00FF66]"}`}>{o.available}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button data-testid={`adjust-minus-${o.sku}`} onClick={() => adjust(o, -1)} className="w-6 h-6 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/5"><Minus size={12} /></button>
                          <button data-testid={`adjust-plus-${o.sku}`} onClick={() => adjust(o, 10)} className="w-auto px-2 h-6 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/5 text-[11px] font-mono">+10</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Dialog open={offOpen} onOpenChange={setOffOpen}>
          <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-xl">Add Product Offering</DialogTitle><DialogDescription className="sr-only">Add a product SKU with transfer price and stock.</DialogDescription></DialogHeader>
            <div className="space-y-3 pt-2">
              <select data-testid="offering-sku-select" className={inputCls} value={offForm.sku}
                onChange={(e) => { const s = skuOptions.find((x) => x.sku === e.target.value); setOffForm((f) => ({ ...f, sku: e.target.value, product_id: s?.product_id || "", product_name: s?.name || "" })); }}>
                <option value="">Select product / SKU…</option>
                {skuOptions.map((s) => <option key={s.sku} value={s.sku}>{s.name} ({s.sku})</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="offering-price-input" className={inputCls} placeholder="Transfer price ₹" type="number" value={offForm.transfer_price} onChange={(e) => setOffForm((f) => ({ ...f, transfer_price: e.target.value }))} />
                <input data-testid="offering-stock-input" className={inputCls} placeholder="Initial stock" type="number" value={offForm.on_hand} onChange={(e) => setOffForm((f) => ({ ...f, on_hand: e.target.value }))} />
              </div>
              <button data-testid="offering-submit-button" onClick={addOffering} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Add Offering</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Fulfillment Hubs" subtitle={`${hubs.length} hubs · owned & partner`}
        actions={<button data-testid="add-hub-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> New Hub</button>} />
      <div className="p-8">
        {hubs.length === 0 ? <EmptyState icon={Warehouse} title="No hubs" hint="Enrol your first fulfillment hub." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hubs.map((h) => (
              <div key={h.id} data-testid={`hub-card-${h.id}`} onClick={() => openHub(h)} className="bg-[#121212] border border-white/10 rounded-sm p-5 hover:border-white/25 transition-colors cursor-pointer">
                <div className="flex items-start justify-between">
                  <div><h3 className="font-display text-lg font-medium">{h.name}</h3><p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {h.city} · {h.pincode}</p></div>
                  <Chip className={h.type === "Owned" ? "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]" : "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]"}>{h.type}</Chip>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(h.capabilities || []).map((c) => <span key={c} className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 border border-white/10 rounded-sm px-1.5 py-0.5">{c}</span>)}
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-display font-bold text-base flex items-center justify-center gap-1"><Boxes size={13} className="text-zinc-500" />{h.sku_count}</div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">SKUs</div></div>
                  <div><div className="font-display font-bold text-base">{h.open_orders}</div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">Open</div></div>
                  <div><div className="font-display font-bold text-base">{h.sla_days}d</div><div className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">SLA</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">New Fulfillment Hub</DialogTitle><DialogDescription className="sr-only">Enrol a fulfillment hub.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="hub-name-input" className={inputCls} placeholder="Hub name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <select data-testid="hub-type-select" className={inputCls} value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="Owned">Owned</option><option value="Partner">Partner</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="hub-city-input" className={inputCls} placeholder="City" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
              <input className={inputCls} placeholder="State" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
              <input data-testid="hub-pincode-input" className={inputCls} placeholder="Pincode *" value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} />
              <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="SLA days" type="number" value={form.sla_days} onChange={(e) => setForm((f) => ({ ...f, sla_days: e.target.value }))} />
              <input className={inputCls} placeholder="Capacity (open orders)" type="number" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Capabilities</div>
              <div className="flex flex-wrap gap-1.5">
                {CAPS.map((c) => <button key={c} type="button" onClick={() => toggleCap(c)} className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${form.capabilities.includes(c) ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:bg-white/5"}`}>{c}</button>)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Brands served (none = all)</div>
              <div className="flex flex-wrap gap-1.5">
                {brands.map((b) => <button key={b.id} type="button" onClick={() => toggleBrand(b.name)} className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${form.brands.includes(b.name) ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:bg-white/5"}`}>{b.name}</button>)}
              </div>
            </div>
            <button data-testid="hub-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Create Hub</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
